/* eslint-disable max-lines */
// Core library dependencies
import { ApiResponse, API_SUCCESS_STATUS_CODE } from "@swiggy-private/core-utils";
import {
    GlobalHeaderState,
    makeDownstreamApiCall,
    PlaceOrderData,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";

// Payment system types and constants
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, OrderApiData } from "@swiggy-private/payment-types";

// Checkout interface types
import * as CheckoutApi from "@payment-client/interfaces/Checkout";

// Order processing service functions
import {
    confirmAssureOrder,
    confirmDashOrder,
    confirmDineoutOrderNew,
    confirmEditOrder,
    confirmIMOrderNew,
    confirmOneOrder,
    confirmRegularOrder,
    confirmSwiggyMoneyOrder,
    placeDashOrder,
    placeEditOrder,
    placeRegularOrder,
    placeSwiggyMoneyOrder,
    confirmSuperOrder,
    placeSwiggySuperOrder,
} from "@payment-client/apis/publicOrderService";
import { placeCBCCPaymentOnboarding } from "@payment-client/apis/CBCCPaymentOnboardingService";
// gifting public apis
import { publicGiftingPlaceOrder } from "@payment-client/apis/PublicGiftingOrderService/place-order/api";
import { publicGiftingConfirmOrder } from "@payment-client/apis/PublicGiftingOrderService/confirm-order/api";

// Order parameter helpers and utilities
import {
    getDashPlaceOrderParams,
    getDineoutPlaceOrderParams,
    getPaymentCode,
    getPlaceOrderConfigParams,
    getSwiggyMoneyOrderPlaceParams,
} from "@payment-client/helpers/PublicOrderUtils";
import { mapPaymentMethodMetaForSwiggySuper } from "@payment-client/helpers/publicOrderExtensions";
import { getCBCCPaymentOnboardingParams } from "@payment-client/helpers/CBCCPaymentOnboardingUtils";

// Header utility functions
import { getMarketplaceBusinessLine, getMarketplaceCategory } from "@payment-client/utils/Headers";

// Order-related types, interfaces, and constants
import {
    ConfirmOrderPublicParams,
    EditOrderConfirmParams,
    ORDER_CONTEXT,
    ORDER_TYPE,
    PlaceOrderParams,
    PlaceOrderPublicParams,
    SwiggyMoneyOrderPlaceParams,
} from "@payment-client/interfaces/OrderPublic";

// Card payment types
import { CREDIT_TYPE } from "@payment-client/interfaces/CardApi";

// Development mock responses
import { mockConfirmOrderResponse, mockPlaceOrderResponse } from "../mocks/order";

export const ENDPOINTS = {
    REGISTER_PAY_ON_DELIVERY: "/api/v3/order/register_pay_on_delivery",
    FETCH_REVIEW_BILL_DETAILS: "/api/v3/emi/review-bill-details",
};

export interface EditOrderPlaceParams extends PlaceOrderParams {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    order_id: string;
    context: ORDER_CONTEXT;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: string;
}

export async function placeOrderPublic(
    apiHost: string,
    params: PlaceOrderPublicParams,
    placeOrderData?: PlaceOrderData,
    isConfirmOnAppLaunchEnabled?: boolean,
): Promise<ApiResponse<OrderApiData>> {
    if (__DEV__) {
        const mockPlaceOrder = document.cookie.indexOf("mock_place_order=1") !== -1;
        if (mockPlaceOrder) {
            return new Promise((resolve) => {
                setTimeout(() => resolve(mockPlaceOrderResponse), 2500);
            });
        }
    }

    const {
        addressId,
        paymentMethod,
        orderComments,
        orderType,
        useJuspayNative,
        paymentReturnUrl,
        cardNumber,
        cvv,
        expiry,
        ..._params
    } = params;

    const headers = GlobalHeaderState.getHeaders();

    // To Add place order meta Data
    const configParams = getPlaceOrderConfigParams(params);

    // Handling for legacy wallet payment method code
    const paymentCode = getPaymentCode(paymentMethod);
    /* eslint-disable @typescript-eslint/naming-convention */
    const data: PlaceOrderParams = {
        address_id: addressId as string,
        payment_cod_method: paymentCode,
        order_comments: orderComments,
        ..._params,
        ...configParams,
    };
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);

    // Handle different marketplace business lines
    switch (marketplaceBusinessLine) {
        case MARKETPLACE_BUSINESS_LINE.EDIT_ORDER:
        case MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP:
        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY: {
            const contextMap = {
                [MARKETPLACE_BUSINESS_LINE.EDIT_ORDER]: ORDER_CONTEXT.EDIT,
                [MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP]: ORDER_CONTEXT.POST_ORDER_TIPPING,
                [MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY]: ORDER_CONTEXT.PAY_ON_DELIVERY,
            };

            const editOrderPlaceParams: EditOrderPlaceParams = {
                ...data,
                context: contextMap[marketplaceBusinessLine],
                cart_key: (headers["cartkey"] as string) || "",
                order_id: headers["orderid"] as string,
            };
            return await placeEditOrder(apiHost, editOrderPlaceParams, isConfirmOnAppLaunchEnabled);
        }
        case MARKETPLACE_BUSINESS_LINE.DASH: {
            const dashPlaceOrderParams = getDashPlaceOrderParams(headers, data);
            return await placeDashOrder(
                apiHost,
                { ...params, ...dashPlaceOrderParams },
                headers,
                isConfirmOnAppLaunchEnabled,
            );
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY: {
            const SwiggyMoneyparams: SwiggyMoneyOrderPlaceParams = getSwiggyMoneyOrderPlaceParams(
                data,
                paymentReturnUrl || "",
            );
            return await placeSwiggyMoneyOrder(apiHost, { ...SwiggyMoneyparams, addressId, paymentMethod });
        }
        case MARKETPLACE_BUSINESS_LINE.DINEOUT: {
            const dineoutPlaceOrderParams = getDineoutPlaceOrderParams(headers, data);
            return await placeDashOrder(
                apiHost,
                { ...params, ...dineoutPlaceOrderParams },
                headers,
                isConfirmOnAppLaunchEnabled,
            );
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER: {
            const swiggySuperParams = mapPaymentMethodMetaForSwiggySuper(
                { ...params, order_type: ORDER_TYPE.SUBSCRIPTION, ...data },
                paymentReturnUrl,
            );
            return await placeSwiggySuperOrder(
                apiHost,
                swiggySuperParams,
                headers,
                isConfirmOnAppLaunchEnabled,
            );
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE: {
            return await placeRegularOrder(
                apiHost,
                { ...params, order_type: ORDER_TYPE.SUBSCRIPTION, ...data },
                headers,
                isConfirmOnAppLaunchEnabled,
            );
        }
        case MARKETPLACE_BUSINESS_LINE.ASSURE: {
            const assurePlaceOrderParams = getDashPlaceOrderParams(headers, data);
            return await placeDashOrder(
                apiHost,
                { ...params, ...assurePlaceOrderParams },
                headers,
                isConfirmOnAppLaunchEnabled,
            );
        }
        case MARKETPLACE_BUSINESS_LINE.CBCC: {
            const cbccPaymentOnboardingParams = getCBCCPaymentOnboardingParams(
                data,
                headers["swiggyapplicationnumber"] as string,
            );
            const cbccResponse = await placeCBCCPaymentOnboarding(cbccPaymentOnboardingParams, headers);

            // Transform CBCC response to match OrderApiData structure
            return {
                ...cbccResponse,
                data: {
                    orderData: {
                        upiIntentUrl: cbccResponse.data?.upiIntentUrl,
                        paymentTxnId: cbccResponse.data?.orderId,
                    },
                },
            } as ApiResponse<OrderApiData>;
        }
        case MARKETPLACE_BUSINESS_LINE.GIFTING: {
            return await publicGiftingPlaceOrder(apiHost, params, placeOrderData);
        }
        default: {
            return await placeRegularOrder(apiHost, { ...params, ...data }, headers, isConfirmOnAppLaunchEnabled);
        }
    }
}

export async function confirmOrderPublic(
    apiHost: string,
    params: ConfirmOrderPublicParams,
): Promise<ApiResponse<OrderApiData>> {
    const headers = GlobalHeaderState.getHeaders();
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const marketplaceCategory = getMarketplaceCategory(headers);

    if (__DEV__) {
        const mockConfirmOrder = document.cookie.indexOf("mock_confirm_order=1") !== -1;
        if (mockConfirmOrder) {
            return new Promise((resolve) => {
                setTimeout(() => resolve(mockConfirmOrderResponse), 2500);
            });
        }
    }

    // Handles confirm order request based on businessline, matching server logic
    switch (marketplaceBusinessLine) {
        case MARKETPLACE_BUSINESS_LINE.EDIT_ORDER:
        case MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP:
        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY: {
            const editOrderParams: EditOrderConfirmParams = {
                order_id: headers["orderid"] as string,
                transaction_id: params.transactionId,
            };

            // Add context and cart_key for non-EDIT_ORDER cases
            if (marketplaceBusinessLine !== MARKETPLACE_BUSINESS_LINE.EDIT_ORDER) {
                const contextMap = {
                    [MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP]: ORDER_CONTEXT.POST_ORDER_TIPPING,
                    [MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY]: ORDER_CONTEXT.PAY_ON_DELIVERY,
                };

                editOrderParams.context = contextMap[marketplaceBusinessLine];
                editOrderParams.cart_key = (headers["cartkey"] as string) || "";
            }

            return await confirmEditOrder(apiHost, editOrderParams);
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE: {
            return await confirmOneOrder(apiHost, String(params.orderId));
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER: {
            return await confirmSuperOrder(apiHost, String(params.orderId));
        }
        case MARKETPLACE_BUSINESS_LINE.DASH: {
            // transactionId, orderId, and handle Instamart
            const dashParams = {
                transactionId: String(params.transactionId),
                orderId: String(params.orderId),
            };

            if (marketplaceCategory === MARKETPLACE_CATEGORY.INSTAMART) {
                return await confirmIMOrderNew(apiHost, dashParams);
            }
            // Instamart handled in service, just pass category
            return await confirmDashOrder(apiHost, dashParams);
        }
        case MARKETPLACE_BUSINESS_LINE.DINEOUT: {
            // transactionId, orderId, confirmOrderXp
            const dineoutParams = {
                transactionId: String(params.transactionId),
                orderId: String(params.orderId),
            };
            return await confirmDineoutOrderNew(apiHost, dineoutParams);
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY: {
            // transactionId, orderId, creditType
            const swiggyMoneyParams = {
                transactionId: String(params.transactionId),
                orderId: String(params.orderId),
                creditType: params.giftDetails ? CREDIT_TYPE.VOUCHER : CREDIT_TYPE.SELFLOAD,
            };
            return await confirmSwiggyMoneyOrder(apiHost, swiggyMoneyParams);
        }
        case MARKETPLACE_BUSINESS_LINE.ASSURE: {
            // transactionId, orderId
            const assureParams = {
                transactionId: String(params.transactionId),
                orderId: String(params.orderId),
            };
            return await confirmAssureOrder(apiHost, assureParams);
        }
        case MARKETPLACE_BUSINESS_LINE.GIFTING: {
            return await publicGiftingConfirmOrder(apiHost, params);
        }
        default: {
            return await confirmRegularOrder(apiHost, {
                orderId: params.orderId,
                transactionId: params.transactionId,
                paymentMethod: (params?.paymentMethod ?? "") as string,
            });
        }
    }
}

export async function registerPayOnDeliveryPublic(
    apiHost: string,
    params: { orderId: string },
): Promise<ApiResponse<CheckoutApi.RequestCartKeyForPayOnDeliveryPayload>> {
    const response = await makeDownstreamApiCall<CheckoutApi.RequestCartKeyForPayOnDeliveryPayload>(
        {
            apiHost,
            endpoint: ENDPOINTS.REGISTER_PAY_ON_DELIVERY,
            bodyParams: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                order_id: params.orderId,
            },
            options: {
                method: "POST",
            },
        },
        false,
        false,
    );

    if (response.statusCode !== API_SUCCESS_STATUS_CODE) {
        throw response;
    } else {
        return response as ApiResponse<CheckoutApi.RequestCartKeyForPayOnDeliveryPayload>;
    }
}

export interface fetchBillDetailsPayload {
    bankCode: string;
    emiTenureInMonths: number;
    emiType: string;
    cardReference: string;
}

export async function fetchBillDetails(
    apiHost: string,
    params: fetchBillDetailsPayload,
): Promise<ApiResponse<CheckoutApi.FetchBillDetailsResponse>> {
    const response = await makeDownstreamApiCall<CheckoutApi.FetchBillDetailsResponse>(
        {
            apiHost,
            endpoint: "/api/v3/emi/review-bill-details",
            bodyParams: {
                cartType: "INSTAMART",
                applyPaymentOptionsRequest: {
                    emiRequest: params,
                },
            },
            options: {
                method: "POST",
                headers: {
                    versioncode: GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode),
                },
            },
        },
        false, // skip key transformation; response keys are already in camelCase
        false,
    );

    if (response.statusCode !== API_SUCCESS_STATUS_CODE) {
        throw response;
    } else {
        return response as ApiResponse<CheckoutApi.FetchBillDetailsResponse>;
    }
}
